import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ChevronLeft, ChevronRight, Package, ArrowDown, ArrowUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarIcon } from "lucide-react";
import { ItemThumbs } from "../components/ItemThumbs";

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const DAYS_ID = ["Min","Sen","Sel","Rab","Kam","Jum","Sab"];

export default function Reservations() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState({});
  const [rentals, setRentals] = useCached("resv_rentals", []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [products, setProducts] = useCached("products", []);
  const [productId, setProductId] = useState("");
  const [blocked, setBlocked] = useState([]);

  const load = () => {
    api.get(`/calendar/month?year=${year}&month=${month}`).then(r => setData(r.data));
    api.get("/rentals").then(r => setRentals(r.data));
    api.get("/products").then(r => setProducts(r.data));
  };
  useEffect(load, [year, month]);

  useEffect(() => {
    if (!productId) { setBlocked([]); return; }
    api.get(`/products/${productId}/blocked-dates`).then(r => setBlocked(r.data.blocked));
  }, [productId]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(y => y-1); } else setMonth(m => m-1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(y => y+1); } else setMonth(m => m+1); };

  // Build grid: array of {date: 'YYYY-MM-DD' | null}
  const first = new Date(year, month-1, 1);
  const firstDow = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const grid = [];
  for (let i = 0; i < firstDow; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    grid.push(iso);
  }

  const todayISO = new Date().toISOString().slice(0,10);
  const dateRentals = (iso) => rentals.filter(r => r.start_date <= iso && r.end_date >= iso && r.status !== "returned");

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Kalender</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Grid Booking Bulanan</h1>
          <p className="text-sm text-neutral-500 mt-1">Klik tanggal untuk lihat detail booking.</p>
        </div>
      </div>

      <Card className="border-neutral-200 shadow-none">
        <CardContent className="p-4 md:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={prev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="font-display font-bold text-xl">{MONTHS_ID[month-1]} {year}</div>
            <Button variant="outline" size="sm" onClick={next} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>

          {/* Optional per-product filter for blocked dates */}
          <div className="mb-4 max-w-sm">
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger><SelectValue placeholder="Filter tanggal habis per alat (opsional)" /></SelectTrigger>
              <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
            {DAYS_ID.map(d => <div key={d} className="text-center text-xs uppercase tracking-wider font-bold text-neutral-500 py-2">{d}</div>)}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-7 gap-1 md:gap-2">
            {grid.map((iso, i) => {
              if (!iso) return <div key={i} className="min-h-[70px] md:min-h-[90px]" />;
              const info = data[iso] || { rentals_count: 0, pickup_count: 0, return_count: 0 };
              const isToday = iso === todayISO;
              const isBlocked = blocked.includes(iso);
              const isPast = iso < todayISO;
              return (
                <button key={iso} onClick={() => setSelectedDate(iso)}
                  data-testid={`cal-date-${iso}`}
                  className={`min-h-[70px] md:min-h-[90px] p-1.5 md:p-2 text-left rounded-md border transition-all hover:shadow-sm ${
                    isBlocked ? "bg-red-50 border-red-300" :
                    info.rentals_count > 0 ? "bg-orange-50 border-orange-200" :
                    isPast ? "bg-neutral-50 border-neutral-100 text-neutral-400" :
                    "bg-white border-neutral-200 hover:border-red-600"
                  } ${isToday ? "ring-2 ring-offset-1" : ""}`}
                  style={isToday ? {"--tw-ring-color": "var(--brand-primary)"} : {}}
                >
                  <div className={`text-sm font-bold ${isToday ? "" : "text-neutral-900"}`} style={isToday ? {color: "var(--brand-primary)"} : {}}>
                    {Number(iso.slice(-2))}
                  </div>
                  {info.rentals_count > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{background: "var(--brand-primary)"}}>{info.rentals_count}</span>
                      <span className="text-[9px] text-neutral-600 hidden md:inline">aktif</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {info.pickup_count > 0 && <span className="text-[9px] font-bold px-1 rounded bg-orange-100 text-orange-800 flex items-center gap-0.5"><ArrowUp className="w-2 h-2" />{info.pickup_count}</span>}
                    {info.return_count > 0 && <span className="text-[9px] font-bold px-1 rounded bg-green-100 text-green-800 flex items-center gap-0.5"><ArrowDown className="w-2 h-2" />{info.return_count}</span>}
                  </div>
                  {isBlocked && <div className="text-[9px] text-red-700 font-bold mt-1">HABIS</div>}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-neutral-600">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-50 border border-orange-200"></span> Ada booking aktif</span>
            <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3 text-orange-800" /> Pengambilan</span>
            <span className="flex items-center gap-1"><ArrowDown className="w-3 h-3 text-green-800" /> Pengembalian</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-300"></span> Alat habis</span>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Detail {selectedDate}</DialogTitle></DialogHeader>
          {selectedDate && (() => {
            const list = dateRentals(selectedDate);
            const pickups = list.filter(r => r.start_date === selectedDate);
            const returns = list.filter(r => r.end_date === selectedDate);
            return (
              <div className="space-y-4">
                {pickups.length > 0 && <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-orange-800 flex items-center gap-1 mb-2"><ArrowUp className="w-3 h-3" /> Pengambilan ({pickups.length})</div>
                  <div className="space-y-2">{pickups.map(r => (
                    <div key={r.id} className="p-3 border border-orange-200 rounded bg-orange-50">
                      <div className="flex justify-between"><div className="font-medium">{r.customer_name}</div><div className="font-mono-tab font-semibold" style={{color: "var(--brand-primary)"}}>{fmtIDR(r.total)}</div></div>
                      <div className="text-xs text-neutral-600 mt-1">{r.code} · {r.items.map(i => `${i.product_name} x${i.quantity}`).join(", ")}</div>
                      <ItemThumbs items={r.items} size={40} />
                    </div>
                  ))}</div>
                </div>}
                {returns.length > 0 && <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-green-800 flex items-center gap-1 mb-2"><ArrowDown className="w-3 h-3" /> Pengembalian ({returns.length})</div>
                  <div className="space-y-2">{returns.map(r => (
                    <div key={r.id} className="p-3 border border-green-200 rounded bg-green-50">
                      <div className="flex justify-between"><div className="font-medium">{r.customer_name}</div><div className="font-mono-tab font-semibold" style={{color: "var(--brand-primary)"}}>{fmtIDR(r.total)}</div></div>
                      <div className="text-xs text-neutral-600 mt-1">{r.code} · {r.items.map(i => `${i.product_name} x${i.quantity}`).join(", ")}</div>
                      <ItemThumbs items={r.items} size={40} />
                    </div>
                  ))}</div>
                </div>}
                {list.filter(r => r.start_date !== selectedDate && r.end_date !== selectedDate).length > 0 && <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-600 flex items-center gap-1 mb-2"><Package className="w-3 h-3" /> Sedang Disewa</div>
                  <div className="space-y-2">{list.filter(r => r.start_date !== selectedDate && r.end_date !== selectedDate).map(r => (
                    <div key={r.id} className="p-3 border border-neutral-200 rounded">
                      <div className="flex justify-between"><div className="font-medium">{r.customer_name}</div><div className="font-mono-tab text-sm text-neutral-500">{r.code}</div></div>
                      <div className="text-xs text-neutral-500 mt-1">{r.start_date} → {r.end_date}</div>
                    </div>
                  ))}</div>
                </div>}
                {list.length === 0 && <div className="text-center py-10 text-neutral-500">Tidak ada booking pada tanggal ini</div>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
